import { useCallback, useEffect, useRef, useState } from 'react';

import { usePaymentAccountSession } from '@/hooks/use-payment-account-session';
import { getApiErrorMessage } from '@/utils/api-error';

export type StripeConnectBootstrapStatus = 'loading' | 'ready' | 'error';

export interface StripeConnectBootstrap {
  status: StripeConnectBootstrapStatus;
  publishableKey: string | null;
  errorMessage: string | null;
  retry: () => void;
  /**
   * Passa direto para `loadConnectAndInitialize` (web e nativo). A primeira
   * chamada devolve o `clientSecret` que já veio do bootstrap (sem round-trip
   * extra); as seguintes — o SDK chama de novo quando a sessão expira —
   * sempre criam uma sessão nova. Nunca guarda o valor devolvido.
   */
  fetchClientSecret: () => Promise<string>;
}

/**
 * Bootstrap comum ao provider web e nativo (T4.4): o SDK do Connect precisa
 * da `publishableKey` de forma síncrona para `loadConnectAndInitialize`, e
 * ela só existe depois de um primeiro `POST /payment-account/session`. Esse
 * primeiro `clientSecret` não é descartado — vira a resposta do primeiro
 * `fetchClientSecret` que o SDK pedir, evitando gastar duas sessões para
 * abrir a tela uma vez.
 */
export function useStripeConnectBootstrap(): StripeConnectBootstrap {
  const { fetchSession } = usePaymentAccountSession();
  const [status, setStatus] = useState<StripeConnectBootstrapStatus>('loading');
  const [publishableKey, setPublishableKey] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const firstSecret = useRef<string | null>(null);
  const attempt = useRef(0);

  const bootstrap = useCallback(() => {
    setStatus('loading');
    setErrorMessage(null);
    setPublishableKey(null);
    firstSecret.current = null;
    const current = ++attempt.current;
    fetchSession()
      .then((session) => {
        if (current !== attempt.current) return;
        firstSecret.current = session.clientSecret;
        setPublishableKey(session.publishableKey);
        setStatus('ready');
      })
      .catch((err) => {
        if (current !== attempt.current) return;
        setErrorMessage(
          getApiErrorMessage(err, 'Não foi possível abrir essa etapa agora. Tente novamente.'),
        );
        setStatus('error');
      });
  }, [fetchSession]);

  useEffect(() => {
    bootstrap();
    // Só na montagem — `retry` refaz sob demanda.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchClientSecret = useCallback(async () => {
    if (firstSecret.current) {
      const secret = firstSecret.current;
      firstSecret.current = null;
      return secret;
    }
    const session = await fetchSession();
    return session.clientSecret;
  }, [fetchSession]);

  return { status, publishableKey, errorMessage, retry: bootstrap, fetchClientSecret };
}
